import { match } from "ts-pattern";
import { ref, type Component, type Ref } from "vue";

import type {
  AutomationId,
  Controller,
  FullEvent,
  FullTask,
  GeneralizedTask,
  MenuItem,
  OrgId,
  Page,
  People,
  RecurrentTask,
} from "@ctrl/controller-rpc";

import {
  AllocationActions,
  allocationActions,
  AutomationActions,
  automationActions,
  BlockActions,
  blockActions,
  EventActions,
  eventActions,
  MenuItemActions,
  menuItemActions,
  ObjectActions,
  objectActions,
  PageActions,
  pageActions,
  PeopleActions,
  peopleActions,
  RecurrentTaskActions,
  recurrentTaskActions,
  TaskActions,
  taskActions,
  type ActionCallback,
  type ContextMenuActionInject,
} from "@/components/actions/actions";
import type { ButtonState } from "@/components/base/BaseButton.vue";
import type { Tint } from "@/components/base/types";
import type { ProseMirrorEditorHandle } from "@/components/editor/prosemirror/editor-handle";
import { isStopped } from "@/components/recurrence";
import { isTaskAssignable } from "@/components/task/assignee";
import { Menu } from "@/composables/useMenu";
import {
  getWorkspace,
  isWorkspaceWritable,
  type UseProfile,
} from "@/composables/useProfile";
import { isNativeTask } from "@/task";
import type { CustomObjectTarget } from "@/types";
import { isTypeMovable } from "@/utils/move";

import { useEventInfo } from "../eventInfo";

type ContextEvent = FullEvent & {
  isCancellable: boolean;
  isDeletable: boolean;
};

type TaskObject = { type: "task"; value: GeneralizedTask };
type RecurrentTaskObject = { type: "task-rec"; value: RecurrentTask };
type AllocationObject = {
  type: "allocation";
  value: FullTask & { event: FullEvent };
};
type EventObject = { type: "event"; value: FullEvent };
type CustomObject = { type: "object"; value: CustomObjectTarget };
type PageObject = { type: "page"; value: Page };
type PeopleObject = { type: "people"; value: People };

export type ObjectContext =
  | TaskObject
  | RecurrentTaskObject
  | AllocationObject
  | EventObject
  | CustomObject
  | PageObject
  | PeopleObject;

export type MenuItemObject = { type: "menu-item"; value: MenuItem };

/**
 * An AI automation as targeted by the automation lists (settings, agent
 * page). Carries what the shared actions need; the owning agent is resolved
 * from the automations index at run time.
 */
export type AutomationTarget = {
  organizationId: OrgId;
  id: AutomationId;
  name: string;
  enabled: boolean;
};

export type AutomationObject = { type: "automation"; value: AutomationTarget };

export type BlockContext = {
  type: "block";
  value: {
    editorView: ProseMirrorEditorHandle;
    startPos: number;
    nodeSize: number;
  };
};
export type Context =
  | ObjectContext
  | BlockContext
  | MenuItemObject
  | AutomationObject;

export type ContextCancellable =
  | Exclude<Context, EventObject>
  | { type: "event"; value: ContextEvent };
type ContextAction<
  ACTIONS_TYPE,
  KEY extends keyof ACTIONS_TYPE,
> = ACTIONS_TYPE[KEY] & {
  group?: "top" | "middle" | "bottom";
  state?: ButtonState;
  tint?: Tint;
};

export type ContextActions =
  | BlockContextActions
  | TaskContextActions
  | RecurrentTaskContextActions
  | AllocationContextActions
  | EventContextActions
  | ObjectContextActions
  | PageContextActions
  | PeopleContextActions
  | MenuItemContextActions;

export type RunnableContextAction = {
  text: string;
  icon: Component;
  shortcuts: Ref<string | string[] | null>;
  shortcutsDisplay: string;
  group?: "top" | "middle" | "bottom";
  state?: ButtonState;
  tint?: Tint;
  run: (inject: ContextMenuActionInject, callback?: ActionCallback) => void;
};

type BindableContextAction<Target> = Omit<RunnableContextAction, "run"> & {
  action: (
    target: Target,
    inject: ContextMenuActionInject,
    callback?: ActionCallback
  ) => void;
};

export const bindContextAction = <Target>(
  target: Target,
  { action, ...metadata }: BindableContextAction<Target>
): RunnableContextAction => ({
  ...metadata,
  run: (inject, callback) => action(target, inject, callback),
});

export async function getActions(
  controller: Controller,
  profile: UseProfile,
  menu: Menu,
  target: ContextCancellable
): Promise<RunnableContextAction[]> {
  return match(target)
    .with({ type: "block" }, (context) =>
      blockContextActions(controller, context.value).map((action) =>
        bindContextAction(context, action)
      )
    )
    .with({ type: "task" }, async (context) =>
      (await taskContextActions(controller, profile, menu, context.value)).map(
        (action) => bindContextAction(context, action)
      )
    )
    .with({ type: "task-rec" }, (context) =>
      recurrentTaskContextActions(controller, menu, context.value).map(
        (action) => bindContextAction(context, action)
      )
    )
    .with({ type: "allocation" }, (context) =>
      allocationContextActions(controller, menu, context.value).map((action) =>
        bindContextAction(context, action)
      )
    )
    .with({ type: "event" }, async (context) =>
      (await eventContextActions(controller, menu, context.value)).map(
        (action) => bindContextAction(context, action)
      )
    )
    .with({ type: "object" }, async (context) =>
      (
        await objectContextActions(controller, profile, menu, context.value)
      ).map((action) => bindContextAction(context, action))
    )
    .with({ type: "page" }, (context) =>
      pageContextActions(controller, menu, context.value).map((action) =>
        bindContextAction(context, action)
      )
    )
    .with({ type: "people" }, (context) =>
      peopleContextActions(controller, menu, context.value).map((action) =>
        bindContextAction(context, action)
      )
    )
    .with({ type: "menu-item" }, (context) =>
      menuItemContextActions(menu, context.value).map((action) =>
        bindContextAction(context, action)
      )
    )
    .with({ type: "automation" }, (context) =>
      automationContextActions(context.value).map((action) =>
        bindContextAction(context, action)
      )
    )
    .exhaustive();
}

export type BlockContextActions = ContextAction<
  BlockActions,
  keyof BlockActions
>;

const blockContextActions = (
  _controller: Controller,
  _block: {
    editorView: ProseMirrorEditorHandle;
    startPos: number;
    nodeSize: number;
  }
): BlockContextActions[] => {
  return [
    { ...blockActions.copyLink, group: "middle" as const },
    { ...blockActions.delete, group: "bottom" as const, tint: "red" as const },
  ];
};
export type TaskContextActions = ContextAction<TaskActions, keyof TaskActions>;
const taskContextActions = async (
  controller: Controller,
  profile: UseProfile,
  menu: Menu,
  task: GeneralizedTask
): Promise<TaskContextActions[]> => {
  const isCompletedOrIgnored = task.completed || task.ignored;
  const isPinned = menu.isItemPinned(task.id);
  // "Assign" only applies to tasks bound to the workspace's provisioned
  // collaboration Task type (integration tasks are bound too, but not
  // assignable).
  const isAssignable = await isTaskAssignable(controller, task);

  // A bound task's row lives in its binding's workspace: a `reader` member
  // cannot write it, so every writing action is shown disabled (native
  // tasks are personal, hence always writable).
  let canWrite = true;
  if (task.binding) {
    const bindingsRef = await controller.task.binding.all();
    const binding = bindingsRef.value?.[task.binding];
    bindingsRef.release();
    canWrite = isWorkspaceWritable(profile, binding?.workspace_id);
  }
  const writeState = canWrite ? ("default" as const) : ("disabled" as const);

  return [
    { ...taskActions.open, group: "top" as const },
    ...(!isCompletedOrIgnored
      ? [
          {
            ...taskActions.complete,
            group: "middle" as const,
            state: writeState,
          },
        ]
      : []),
    ...(!isCompletedOrIgnored
      ? [{ ...taskActions.ignore, group: "middle" as const, state: writeState }]
      : []),
    ...(isCompletedOrIgnored
      ? [
          {
            ...taskActions.restore,
            group: "middle" as const,
            state: writeState,
          },
        ]
      : []),
    ...(!isCompletedOrIgnored
      ? [{ ...taskActions.plan, group: "middle" as const, state: writeState }]
      : []),
    {
      ...taskActions.move,
      group: "middle" as const,
      state: task.canMove === false || !canWrite ? "disabled" : "default",
    },
    ...(isAssignable
      ? [{ ...taskActions.assign, group: "middle" as const, state: writeState }]
      : []),
    {
      ...taskActions.togglePin,
      group: "bottom" as const,
      text: isPinned ? "Unpin" : "Pin",
    },
    // Recurrence only exists for native tasks.
    ...(isNativeTask(task)
      ? [{ ...taskActions.repeat, group: "bottom" as const }]
      : []),
    { ...taskActions.duplicate, group: "bottom" as const, state: writeState },
    { ...taskActions.copyLink, group: "bottom" as const },
    {
      ...taskActions.delete,
      group: "bottom" as const,
      tint: "red" as const,
      state:
        task.canDelete === false || !canWrite
          ? ("disabled" as const)
          : ("default" as const),
    },
  ];
};
export type RecurrentTaskContextActions = ContextAction<
  RecurrentTaskActions,
  keyof RecurrentTaskActions
>;
const recurrentTaskContextActions = (
  controller: Controller,
  menu: Menu,
  recurrentTask: RecurrentTask
): RecurrentTaskContextActions[] => {
  const isPinned = menu.isItemPinned(recurrentTask.id);
  return [
    { ...recurrentTaskActions.open, group: "top" },
    isStopped(recurrentTask)
      ? { ...recurrentTaskActions.resume, group: "middle" }
      : { ...recurrentTaskActions.pause, group: "middle" },
    { ...recurrentTaskActions.editRecurrence, group: "middle" },
    { ...recurrentTaskActions.move, group: "bottom" },
    {
      ...recurrentTaskActions.togglePin,
      group: "bottom" as const,
      text: isPinned ? "Unpin" : "Pin",
    },
    { ...recurrentTaskActions.copyLink, group: "bottom" },
    { ...recurrentTaskActions.delete, group: "bottom", tint: "red" },
  ];
};

export type AllocationContextActions = ContextAction<
  AllocationActions,
  keyof AllocationActions
>;
const allocationContextActions = (
  controller: Controller,
  menu: Menu,
  allocation: FullTask & { event: FullEvent }
): AllocationContextActions[] => {
  return [
    { ...allocationActions.open, group: "top" },
    allocation.completed
      ? { ...allocationActions.uncomplete, group: "middle" }
      : { ...allocationActions.complete, group: "middle" },
    { ...allocationActions.ignore, group: "middle" },
    { ...allocationActions.copyLink, group: "bottom" },
    { ...allocationActions.delete, group: "bottom", tint: "red" },
  ];
};
export type EventContextActions = ContextAction<
  EventActions,
  keyof EventActions
>;
const eventContextActions = async (
  controller: Controller,
  menu: Menu,
  event: ContextEvent
): Promise<EventContextActions[]> => {
  const isPinned = menu.isItemPinned(event.id);
  const fullEvent = ref<FullEvent>(event);
  const { isMeetingWithMe } = await useEventInfo(
    controller,
    undefined,
    fullEvent
  );
  const isAttendable = event.attendable;
  const attendanceActions =
    isAttendable && isMeetingWithMe.value
      ? [
          eventActions.attendYes,
          eventActions.attendMaybe,
          eventActions.attendNo,
        ]
      : [];
  return [
    { ...eventActions.open, group: "top" as const },
    ...(isAttendable ? [eventActions.openConference] : []),
    ...attendanceActions,
    ...(event.editable && event.end.getTime() > Date.now()
      ? [{ ...eventActions.complete, group: "middle" as const }]
      : []),
    event.active
      ? { ...eventActions.setAsPassive, group: "middle" as const }
      : { ...eventActions.setAsActive, group: "middle" as const },
    event.available === "busy"
      ? { ...eventActions.markAsFree, group: "middle" as const }
      : { ...eventActions.markAsBusy, group: "middle" as const },
    {
      ...eventActions.togglePin,
      group: "bottom" as const,
      text: isPinned ? "Unpin" : "Pin",
    },
    ...(!event.draft
      ? [{ ...eventActions.duplicate, group: "bottom" as const }]
      : []),
    ...(!event.draft
      ? [{ ...eventActions.copyLink, group: "bottom" as const }]
      : []),
    ...(event.isDeletable
      ? [
          {
            ...eventActions.delete,
            group: "bottom" as const,
            tint: "red" as const,
          },
        ]
      : []),
  ];
};
export type ObjectContextActions = ContextAction<
  ObjectActions,
  keyof ObjectActions
>;
const objectContextActions = async (
  controller: Controller,
  profile: UseProfile,
  menu: Menu,
  object: CustomObjectTarget
): Promise<ObjectContextActions[]> => {
  const wsId = object.workspace_id;
  let isHome = false;
  let hasHome = false;
  if (wsId) {
    // A `reader` member of the object's workspace cannot write it: the
    // writing actions (Move, Duplicate, Set home, Delete) are shown
    // disabled, like the workspace-home and bound-task restrictions —
    // matching CustomObjectTopBar's "..." menu.
    const canWrite = isWorkspaceWritable(profile, wsId);
    const fullId = await controller.graph.object.id.make({
      workspace_id: wsId,
      table_id: object.table_id,
      row_id: object.row_id,
    });
    const isPinned = menu.isItemPinned(fullId);
    const ws = getWorkspace(profile, wsId)?.workspace;
    const wsHome = ws?.home ?? null;
    const isPersonal = ws?.personal === true;
    hasHome = wsHome !== null;
    isHome = wsHome === fullId;

    // "Move" must be disabled when the object's type is a built-in one
    // (Agent, Automation, Notice, ...) whose objects cannot be re-parented.
    const database = (
      await controller.graph.database.get(wsId, object.table_id)
    ).release();
    const typeMovable = !database || isTypeMovable(database);

    // A bound task's source table determines whether its row may be moved or
    // deleted. Resolve the task capabilities so user-owned workspace rows stay
    // actionable while integration/internal rows and read-only tables remain
    // protected. Degrade gracefully for non-task objects or unsupported ids.
    let boundCanMove: boolean | undefined;
    let boundCanDelete: boolean | undefined;
    const bindingsRef = await controller.task.binding.all();
    const bindings = bindingsRef.value;
    bindingsRef.release();
    const hasBinding = bindings
      ? Object.values(bindings).some(
          (b) => b.workspace_id === wsId && b.table_id === object.table_id
        )
      : false;
    if (hasBinding) {
      try {
        const taskRef = await controller.task.get(fullId);
        boundCanMove = taskRef.value?.canMove;
        boundCanDelete = taskRef.value?.canDelete;
        taskRef.release();
      } catch {
        // Not a task-backed object id; leave the object movable/deletable.
      }
    }

    return [
      { ...objectActions.open, group: "top" },
      // The workspace home object must stay in place: Move and Delete are
      // shown disabled (like bound-task restrictions), not hidden.
      {
        ...objectActions.move,
        group: "bottom" as const,
        state:
          isHome || boundCanMove === false || !typeMovable || !canWrite
            ? ("disabled" as const)
            : ("default" as const),
      },
      {
        ...objectActions.togglePin,
        group: "bottom" as const,
        text: isPinned ? "Unpin" : "Pin",
      },
      { ...objectActions.copyLink, group: "bottom" },
      {
        ...objectActions.duplicate,
        group: "bottom" as const,
        state: canWrite ? ("default" as const) : ("disabled" as const),
      },
      ...(!hasHome && !isPersonal
        ? [
            {
              ...objectActions.setHome,
              group: "bottom" as const,
              state: canWrite ? ("default" as const) : ("disabled" as const),
            },
          ]
        : []),
      {
        ...objectActions.delete,
        group: "bottom" as const,
        tint: "red" as const,
        state:
          isHome || boundCanDelete === false || !canWrite
            ? ("disabled" as const)
            : ("default" as const),
      },
    ];
  }
  return [];
};
export type PageContextActions = ContextAction<PageActions, keyof PageActions>;
const pageContextActions = (
  _controller: Controller,
  menu: Menu,
  _page: Page
): PageContextActions[] => {
  const isPinned = menu.isItemPinned(_page.id);
  return [
    { ...pageActions.open, group: "top" },
    {
      ...pageActions.togglePin,
      group: "bottom" as const,
      text: isPinned ? "Unpin" : "Pin",
    },
    { ...pageActions.copyLink, group: "bottom" },
    { ...pageActions.duplicate, group: "bottom" },
    { ...pageActions.move, group: "bottom" },
    { ...pageActions.delete, group: "bottom", tint: "red" },
  ];
};
export type PeopleContextActions = ContextAction<
  PeopleActions,
  keyof PeopleActions
>;
const peopleContextActions = (
  controller: Controller,
  menu: Menu,
  _people: People
): PeopleContextActions[] => {
  const isPinned = menu.isItemPinned(_people.id);
  return [
    { ...peopleActions.open, group: "top" },
    { ...peopleActions.invite, group: "middle" },
    {
      ...peopleActions.togglePin,
      group: "bottom" as const,
      text: isPinned ? "Unpin" : "Pin",
    },
    { ...peopleActions.copyLink, group: "bottom" },
  ];
};

export type MenuItemContextActions = ContextAction<
  MenuItemActions,
  keyof MenuItemActions
>;
const menuItemContextActions = (
  _menu: Menu,
  _menuItem: MenuItem
): MenuItemContextActions[] => {
  return [{ ...menuItemActions.unpin, group: "middle" }];
};

export type AutomationContextActions = ContextAction<
  AutomationActions,
  keyof AutomationActions
>;
const automationContextActions = (
  automation: AutomationTarget
): AutomationContextActions[] => {
  return [
    { ...automationActions.open, group: "top" },
    {
      ...automationActions.run,
      group: "middle",
      // Matches the "..." menu: a disabled automation cannot be run.
      state: automation.enabled ? "default" : "disabled",
    },
    automation.enabled
      ? { ...automationActions.disable, group: "middle" }
      : { ...automationActions.enable, group: "middle" },
    { ...automationActions.duplicate, group: "bottom" },
    { ...automationActions.copyLink, group: "bottom" },
    { ...automationActions.delete, group: "bottom", tint: "red" },
  ];
};
